/* eslint-disable @typescript-eslint/naming-convention */
import { FeatureFlag } from '@ps/web-core-enums';
import { omit } from 'lodash';

import { MoneyOption } from '@swf/core/enums';
import type { EffectParams, ModelConfig, Slicer } from '@swf/core/store';
import type { CasinoGame } from '@swf/core/types/CasinoGame';

export interface SearchState {
  search: CasinoGame[];
  query: string;
  qry: unknown[];
  includeTags: string[];
}

const defaultSearchState: SearchState | null = null;

const reducers = {
  set: (state: SearchState | null, payload: SearchState) => payload,
  clear: () => defaultSearchState
};

const effects = {
  async execute(payload: string, { request, dispatch, select, rootState }: EffectParams) {
    const isPlayMoney = select.currency.isPlayMoney(rootState);
    const moneyOption = isPlayMoney ? MoneyOption.Play : MoneyOption.Real;
    const siteName = select.cms.getSiteName(rootState);
    const sharedParams = { operand: 1, join: 0, dynamic: false };
    const infoEnabled = select.feature.check(rootState, FeatureFlag.CasinoGameInfo);
    const search = select.cms.getSearch(rootState);
    let includeTags = search?.include_tags
      ? search.include_tags
      : [
          `Race ${siteName}`,
          `Exclusive ${siteName}`,
          `New ${siteName}`,
          `multiplier100 ${siteName}`,
          `multiplier300 ${siteName}`,
          `multiplier500 ${siteName}`,
          `multiplier1000 ${siteName}`,
          `multiplier2000 ${siteName}`,
          `multiplier2500 ${siteName}`,
          `multiplier5000 ${siteName}`,
          `multiplier7500 ${siteName}`,
          `multiplier10000 ${siteName}`,
          `multiplier15000 ${siteName}`,
          `multiplier20000 ${siteName}`,
          `Hot ${siteName}`,
          `High Roller`,
          `o_Exclusive ${siteName}`,
          `Drops & Wins ${siteName}`,
          `Partypots`,
          `Jackpot King ${siteName}`,
          `Must Go ${siteName}`
        ];

    if (!infoEnabled) {
      includeTags = includeTags.filter((item) => item !== 'GameInfo');
    }

    const qry = [
      ...(search?.qry.map((item) =>
        omit(
          {
            ...item,
            ...(item.exclude_query_values.length && { values: item.exclude_query_values })
          },
          ['exclude_query_values', '_metadata']
        )
      ) || []),
      { ...sharedParams, formalparamcode: 'moneyoption', values: [moneyOption] },
      { ...sharedParams, formalparamcode: 'search_text', values: [payload] }
    ];

    const { body: data } = await request<CasinoGame[]>({
      service: 'casino.games.all',
      body: {
        pg: 0,
        psz: 30,
        ims: { tags: ['icnSquare', 'icnForeground', 'icnBackground'] },
        qry,
        grp: true,
        tgs: true,
        includeTags,
        order: [{ column: 'numOf', sort: 1 }]
      }
    });

    dispatch.search.set({
      search: data,
      query: payload,
      qry,
      includeTags
    });
  }
};

const selectors = (slice: Slicer<SearchState | null>) => ({
  get() {
    return slice;
  }
});

export interface SearchModel extends ModelConfig<SearchState | null> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const search: SearchModel = {
  state: defaultSearchState,
  reducers,
  effects,
  selectors
};

export default search;
